import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import {
  isResizableImageUrl,
  resizedImageSrc,
  resizedImageSrcSet,
} from "@/lib/image-url"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Display-sized src for a cover/banner image. Two CDNs, two mechanisms:
//   - Unsplash hotlinks resize at their own CDN via `fm`/`w` params (free, no
//     compute on our side) — keep all other params (ixid, ixlib, crop) intact.
//   - Our own R2 uploads have NO resizing CDN in front of the public `.r2.dev`
//     domain, so they serve WebP copies written at upload time (see
//     image-url.ts). This is why a 4 MB R2 banner arrives display-sized.
// Anything else (data: url, etc.) passes through unchanged.
// (Name kept for its many existing callers; it now covers both CDNs.)
export function unsplashWebp(url: string, width = 800): string {
  if (isResizableImageUrl(url)) return resizedImageSrc(url, width)
  if (!url.includes(`images.unsplash.com`)) return url
  try {
    const u = new URL(url)
    u.searchParams.set(`fm`, `webp`)
    u.searchParams.set(`w`, String(width))
    return u.toString()
  } catch {
    return url
  }
}

// Builds a srcSet across a range of widths so the browser downloads the size
// that actually fits the slot (and the device DPR) instead of one fixed width —
// a 300px desktop card no longer pulls an 800px file. Resolves Unsplash via its
// CDN params and our R2 uploads via their stored copies. Returns undefined for
// URLs neither CDN can resize so the caller's plain `src` is used unchanged.
export function unsplashSrcSet(
  url: string,
  widths: number[] = [400, 600, 800, 1200]
): string | undefined {
  if (isResizableImageUrl(url)) return resizedImageSrcSet(url)
  if (!url.includes(`images.unsplash.com`)) return undefined
  return widths.map((w) => `${unsplashWebp(url, w)} ${w}w`).join(`, `)
}
